import logger from "../../utils/logger";
import { supabase } from './client';

/**
 * Generowanie unikalnego tokenu zaproszenia
 * Używamy prostego generatora UUID, w produkcji można użyć nanoid
 */
function generateInvitationToken() {
  return crypto.randomUUID().replace(/-/g, '') + Date.now().toString(36);
}

/**
 * Wysłanie zaproszenia do najemcy
 * (Na razie tylko tworzy rekord w DB, email będzie później)
 */
export async function inviteTenant(propertyId, tenantEmail, permissions = {}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Użytkownik niezalogowany');

  // Sprawdź czy użytkownik ma plan Pro
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('subscription_plan')
    .eq('id', user.id)
    .single();

  if (profileError) throw profileError;

  if (profile.subscription_plan !== 'pro') {
    throw new Error('Funkcja zapraszania najemców dostępna tylko w planie Pro');
  }

  // Generuj unikalny token
  const token = generateInvitationToken();

  // Wygaśnięcie za 7 dni
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7);

  const { data, error } = await supabase
    .from('tenant_invitations')
    .insert([{
      property_id: propertyId,
      tenant_email: tenantEmail,
      invited_by: user.id,
      invitation_token: token,
      permissions: {
        view_inspections: permissions.viewInspections !== false,
        upload_photos: permissions.uploadPhotos || false,
      },
      expires_at: expiresAt.toISOString(),
    }])
    .select()
    .single();

  if (error) throw error;

  // TODO: Wysłać email z linkiem: /accept-invitation?token={token}

  return data;
}

/**
 * Pobranie zaproszeń dla nieruchomości
 */
export async function getPropertyInvitations(propertyId) {
  const { data, error } = await supabase
    .from('tenant_invitations')
    .select('*')
    .eq('property_id', propertyId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Anulowanie zaproszenia
 */
export async function revokeInvitation(invitationId) {
  const { error } = await supabase
    .from('tenant_invitations')
    .update({ status: 'revoked' })
    .eq('id', invitationId);

  if (error) throw error;
}

/**
 * Usunięcie wszystkich aktywnych zaproszeń dla nieruchomości
 * (używane przy usuwaniu/archiwizacji umowy)
 */
export async function revokeAllInvitationsForProperty(propertyId) {
  const { error } = await supabase
    .from('tenant_invitations')
    .update({ status: 'revoked' })
    .eq('property_id', propertyId)
    .in('status', ['pending', 'accepted']);

  if (error) throw error;
}

/**
 * Akceptacja zaproszenia przez najemcę (stara wersja)
 * @deprecated Użyj acceptInvitationWithAuth() zamiast tego
 */
export async function acceptInvitation(token) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Użytkownik niezalogowany');

  // Znajdź zaproszenie po tokenie
  const { data: invitation, error: fetchError } = await supabase
    .from('tenant_invitations')
    .select('*')
    .eq('invitation_token', token)
    .eq('status', 'pending')
    .single();

  if (fetchError || !invitation) {
    throw new Error('Nieprawidłowe lub wygasłe zaproszenie');
  }

  // Sprawdź czy nie wygasło
  if (new Date(invitation.expires_at) < new Date()) {
    throw new Error('Zaproszenie wygasło');
  }

  // Zaktualizuj status
  const { error: updateError } = await supabase
    .from('tenant_invitations')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString(),
    })
    .eq('id', invitation.id);

  if (updateError) throw updateError;

  return invitation;
}

/**
 * Akceptacja zaproszenia z powiązaniem do konta użytkownika
 * Dodaje tenant_user_id do zaproszenia i weryfikuje email
 */
export async function acceptInvitationWithAuth(token) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Użytkownik niezalogowany');

  // Znajdź zaproszenie po tokenie (bez JOIN - unikamy RLS konfliktów)
  const { data: invitation, error: fetchError } = await supabase
    .from('tenant_invitations')
    .select('*')
    .eq('invitation_token', token)
    .eq('status', 'pending')
    .single();

  if (fetchError || !invitation) {
    throw new Error('Nieprawidłowe lub wygasłe zaproszenie');
  }

  // Pobierz property osobno
  const { data: property, error: propertyError } = await supabase
    .from('properties')
    .select('*')
    .eq('id', invitation.property_id)
    .single();

  if (!propertyError && property) {
    invitation.properties = property;
  }

  // Sprawdź czy nie wygasło
  if (new Date(invitation.expires_at) < new Date()) {
    throw new Error('Zaproszenie wygasło');
  }

  // Weryfikacja: email użytkownika musi się zgadzać z emailem w zaproszeniu
  if (invitation.tenant_email.toLowerCase() !== user.email.toLowerCase()) {
    throw new Error('Zaproszenie zostało wysłane na inny adres email');
  }

  // Zaktualizuj status i dodaj powiązanie z kontem użytkownika
  const { error: updateError } = await supabase
    .from('tenant_invitations')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString(),
      tenant_user_id: user.id,
    })
    .eq('id', invitation.id);

  if (updateError) throw updateError;

  return {
    ...invitation,
    tenant_user_id: user.id,
  };
}

/**
 * Pobranie nieruchomości do których użytkownik ma dostęp jako najemca
 */
export async function getTenantProperties() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Użytkownik niezalogowany');

  logger.log('[getTenantProperties] Fetching tenant properties');

  // Najpierw pobierz zaproszenia (bez nested select by uniknąć rekursji RLS)
  const { data: invitations, error: invError } = await supabase
    .from('tenant_invitations')
    .select('id, property_id, permissions, accepted_at, tenant_email, status')
    .eq('tenant_user_id', user.id)
    .eq('status', 'accepted')
    .order('accepted_at', { ascending: false });

  logger.log('[getTenantProperties] Invitations found:', invitations?.length ?? 0);

  if (invError) {
    console.error('[getTenantProperties] Error fetching invitations:', invError);
    throw invError;
  }

  if (!invitations || invitations.length === 0) {
    logger.log('[getTenantProperties] No invitations found - returning empty array');
    return [];
  }

  // Następnie pobierz properties po ID
  // RLS używa funkcji user_has_tenant_access() która nie powoduje rekursji
  const propertyIds = invitations.map(inv => inv.property_id);
  logger.log('[getTenantProperties] Fetching properties:', propertyIds.length);

  const { data: properties, error: propError } = await supabase
    .from('properties')
    .select('*')
    .in('id', propertyIds);

  logger.log('[getTenantProperties] Properties found:', properties?.length ?? 0);

  if (propError) {
    console.error('[getTenantProperties] Error fetching properties:', propError);
    throw propError;
  }

  // Połącz dane
  const result = invitations.map(inv => {
    const property = properties?.find(p => p.id === inv.property_id);
    if (!property) {
      console.warn('[getTenantProperties] Property not found for invitation:', inv.property_id);
    }
    return {
      ...property,
      invitation: {
        id: inv.id,
        permissions: inv.permissions,
        acceptedAt: inv.accepted_at,
      },
    };
  }).filter(p => p.id); // Usuń te gdzie property nie został znaleziony

  logger.log('[getTenantProperties] Final result:', result.length, 'properties');
  return result;
}

/**
 * Sprawdź czy użytkownik ma dostęp jako najemca do danej nieruchomości
 */
export async function canTenantAccessProperty(propertyId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Użytkownik niezalogowany');

  const { data, error } = await supabase
    .from('tenant_invitations')
    .select('permissions')
    .eq('property_id', propertyId)
    .eq('tenant_user_id', user.id)
    .eq('status', 'accepted')
    .single();

  if (error || !data) {
    return {
      allowed: false,
      reason: 'Brak dostępu do tej nieruchomości',
    };
  }

  if (!data.permissions?.upload_photos) {
    return {
      allowed: false,
      reason: 'Brak uprawnień do dodawania inspekcji',
      permissions: data.permissions,
    };
  }

  return {
    allowed: true,
    permissions: data.permissions,
  };
}

/**
 * Pobranie pojedynczego zaproszenia dla użytkownika i nieruchomości
 * Pomocnicze dla usePropertyRole hook
 */
export async function getTenantInvitation(propertyId, userId) {
  const { data, error } = await supabase
    .from('tenant_invitations')
    .select('*')
    .eq('property_id', propertyId)
    .eq('tenant_user_id', userId)
    .eq('status', 'accepted')
    .maybeSingle();

  if (error) throw error;
  return data;
}
